import type { HTMLAttributes } from "react";

import { cn } from "@/lib/utils";

export type ClientMetricsItem = {
  id?: string;
  label: string;
  value: string;
  valueClassName?: string;
  /** Emphasized footer row (e.g. Total Trapped Margin). */
  emphasized?: boolean;
};

export type ClientMetricsListProps = {
  items: ClientMetricsItem[];
} & HTMLAttributes<HTMLDListElement>;

/**
 * Dark TCI label/value metrics panel (Figma 378:1896 / 849:8803).
 * Row count follows `items`.
 */
export function ClientMetricsList({
  items,
  className,
  ...props
}: ClientMetricsListProps) {
  return (
    <dl
      className={cn(
        "flex w-full flex-col gap-4 border border-sky-400/35 bg-[rgba(11,22,40,0.42)] pt-6 backdrop-blur-[8px]",
        className,
      )}
      {...props}
    >
      {items.map((item, index) => {
        const key = item.id ?? `${item.label}-${index}`;
        const isLast = index === items.length - 1;
        const emphasized = Boolean(item.emphasized);
        const nextIsEmphasized = Boolean(items[index + 1]?.emphasized);

        return (
          <div key={key} className="contents">
            {emphasized ? (
              <div className="flex h-[52px] w-full items-center gap-[9px] border-t border-[rgba(56,189,248,0.35)] bg-[#0b1628] px-5">
                <dt className="min-w-0 flex-1 break-words font-['Inter'] text-sm font-medium leading-5 tracking-[-0.35px] text-[rgba(255,255,255,0.50)]">
                  {item.label}
                </dt>
                <dd
                  className={cn(
                    "max-w-[45%] shrink-0 break-words text-right font-['Inter'] text-sm font-extrabold leading-5 tracking-[-0.35px] text-[#22C55E] sm:max-w-none sm:whitespace-nowrap",
                    item.valueClassName,
                  )}
                >
                  {item.value}
                </dd>
              </div>
            ) : (
              <>
                <div className="flex w-full items-start gap-[9px] px-5">
                  <dt className="min-w-0 flex-1 break-words font-['Inter'] text-sm font-medium leading-5 tracking-[-0.02188rem] text-[rgba(255,255,255,0.50)]">
                    {item.label}
                  </dt>
                  <dd
                    className={cn(
                      "max-w-[45%] shrink-0 break-words text-right font-['Inter'] text-sm font-medium leading-5 tracking-[-0.02188rem] text-white sm:max-w-none sm:whitespace-nowrap",
                      item.valueClassName,
                    )}
                  >
                    {item.value}
                  </dd>
                </div>
                {!isLast && !nextIsEmphasized ? (
                  <div
                    aria-hidden
                    className="h-px w-full shrink-0 bg-sky-400/35"
                  />
                ) : null}
              </>
            )}
          </div>
        );
      })}
    </dl>
  );
}
